import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User } from '../api/authApi';
import { impersonateUserByUid } from '../auth/impersonateUser';
import GetInTouchPanel from '@/components/GetInTouchPanel';
import { UserCog, Mail, FileText, Plus, Radio, Inbox, Database } from 'lucide-react';
import {
    clearClient404Log,
    fetchClient404PathBreakdown,
    fetchClient404Stats,
    type Client404PathBreakdown,
    type Client404Stats,
} from '../api/client404Api';
import {
    fetchMyFormSubmissions,
    MyFormSubmissionEntry,
} from '../api/publicFormsApi';
import { SubmissionListEntryContent } from '../public/submissionListEntryContent';
import { ActionTypes } from '@/actions';
import { usePermission } from '../acl/usePermission';
import { toast } from 'react-toastify';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Person2 } from '@mui/icons-material';
import { QuickAddDashboardSetting } from '../inbox/QuickAddDashboardSetting';
import { AssignInboxItemDialog } from '../inbox/AssignInboxItemDialog';
import { deleteInboxItem, listInboxItems, type InboxItem } from '../api/inboxApi';
import { isUiSectionHidden } from './uiSettings';
import { readUserType } from './userType';
import { buildCollectionPath } from '../utils/collectionPaths';
import { CollectionMenuIcon } from '../nav/CollectionMenuIcon';
import { EMPTY_ARRAY } from '@/constants/reduxStableRefs';

import { resolveApiBaseUrl } from '../api/apiBase';

const API_BASE_URL = resolveApiBaseUrl();

function formatInboxDate(createdAt: string | undefined): string {
    if (!createdAt) return '';
    try {
        const d = new Date(createdAt);
        return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return createdAt;
    }
}

function format404HitTime(iso: string | undefined): string {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}

const Home: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useAuth();
    const hideInbox = isUiSectionHidden(user, 'hideInbox');
    const hideSubmissions = isUiSectionHidden(user, 'hideSubmissions');
    const isWebsiteOwner = readUserType(user) === 'website_owner';
    const components = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY);
    const componentsLoading = useSelector((state: any) => state.components.loading) || false;
    const componentsError = useSelector((state: any) => state.components.error) || null;
    const [isAdmin, setIsAdmin] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [formSubmissions, setFormSubmissions] = useState<MyFormSubmissionEntry[]>([]);
    const [formSubmissionsLoading, setFormSubmissionsLoading] = useState(true);
    const [formSubmissionsError, setFormSubmissionsError] = useState<string | null>(null);
    const [showAllSubmissions, setShowAllSubmissions] = useState(false);
    const [inboxItems, setInboxItems] = useState<InboxItem[]>([]);
    const [inboxAssignItem, setInboxAssignItem] = useState<InboxItem | null>(null);
    const [inboxDeleteItem, setInboxDeleteItem] = useState<InboxItem | null>(null);
    const [inboxDeleting, setInboxDeleting] = useState(false);
    const reloadConfigNonce = useSelector((state: any) => state.ui?.reloadConfigNonce ?? 0);

    const userId = useMemo(() => {
        if (!user) return null;
        // Prefer top-level IDs
        const directId = user.uid || user.id || user._id;
        if (directId) return directId;
        // Fallback to nested info identifiers if present
        const info: any = user.info || {};
        return info.uid || info.id || info._id || null;
    }, [user]);

    const createCollectionRequest = useMemo(
        () =>
            user
                ? { action: 'create_collection' as const, targetType: 'collection' as const, targetId: '*' }
                : null,
        [user],
    );
    const { allowed: canCreateCollection } = usePermission(createCollectionRequest, {
        enabled: Boolean(user),
    });
    const limit = user?.collectionLimit ?? user?.info?.collectionLimit;
    const count = user?.collectionCount ?? user?.info?.collectionCount ?? 0;
    const hasNoLimit = limit == null;
    const atLimit = typeof limit === 'number' && limit > 0 && count >= limit;
    const isAdminUser = Boolean(user?.admin || user?.info?.admin);
    const canCreateCollectionByLimit = isAdminUser || (!hasNoLimit && !atLimit);

    const navigateCreateCollection = () => {
        if (!user) return;
        if (!canCreateCollectionByLimit) {
            const message = hasNoLimit
                ? 'You cannot create collections yet. Please wait for an administrator to give you access.'
                : 'You cannot create more collections. Please wait for an administrator to increase your limit or grant access.';
            toast.warn(message, { autoClose: 8000 });
            return;
        }
        if (isAdminUser) {
            navigate('/admin/collections', { state: { create: true } });
        } else {
            navigate('/components', { state: { create: true } });
        }
    };

    useEffect(() => {
        if (!userId || !user) return;
        dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } });
    }, [userId, user, dispatch]);

    useEffect(() => {
        if (!userId || hideSubmissions || isWebsiteOwner) {
            setFormSubmissions([]);
            setFormSubmissionsLoading(false);
            return;
        }
        const load = async () => {
            setFormSubmissionsLoading(true);
            setFormSubmissionsError(null);
            try {
                const list = await fetchMyFormSubmissions(50);
                setFormSubmissions(list);
            } catch (err: unknown) {
                setFormSubmissions([]);
                setFormSubmissionsError(err instanceof Error ? err.message : 'Failed to load form submissions');
            } finally {
                setFormSubmissionsLoading(false);
            }
        };
        load();
    }, [userId, reloadConfigNonce, hideSubmissions, isWebsiteOwner]);

    useEffect(() => {
        if (!userId || hideInbox) {
            setInboxItems([]);
            return;
        }
        const loadInbox = async () => {
            try {
                const items = await listInboxItems();
                setInboxItems(items);
            } catch {
                setInboxItems([]);
            }
        };
        void loadInbox();
        const handler = () => {
            void loadInbox();
        };
        window.addEventListener('inbox-updated', handler);
        return () => window.removeEventListener('inbox-updated', handler);
    }, [userId, reloadConfigNonce, hideInbox]);

    const removeInboxItemFromList = (uid: string) => {
        setInboxItems((current) => current.filter((item) => item.uid !== uid));
    };

    const handleInboxDeleteConfirm = async () => {
        if (!inboxDeleteItem) return;
        setInboxDeleting(true);
        try {
            await deleteInboxItem(inboxDeleteItem.uid);
            removeInboxItemFromList(inboxDeleteItem.uid);
            window.dispatchEvent(new CustomEvent('inbox-updated'));
            toast.success('Inbox item deleted');
            setInboxDeleteItem(null);
        } catch (err: unknown) {
            const errorObj = err as Error & { status?: number };
            if (errorObj.status === 404) {
                removeInboxItemFromList(inboxDeleteItem.uid);
                setInboxDeleteItem(null);
                return;
            }
            toast.error(errorObj.message || 'Could not delete item');
        } finally {
            setInboxDeleting(false);
        }
    };

    useEffect(() => {
        const checkAdmin = () => {
            const adminValue = user?.admin || user?.info?.admin;
            setIsAdmin(!!adminValue);
        };
        checkAdmin();
    }, [user]);

    useEffect(() => {
        const fetchUsers = async () => {
            if (!isAdmin) return;

            try {
                const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                const response = await fetch(`${API_BASE_URL}/users/peers`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch users' }));
                    throw new Error(errorData.message || `HTTP ${response.status}`);
                }

                const data = await response.json();
                const usersList: any[] = Array.isArray(data.peers) ? data.peers : (Array.isArray(data) ? data : []);

                const usersData: User[] = usersList.map((user: any) => {
                    const firstName = user.firstName || user.info?.firstName || '';
                    const lastName = user.lastName || user.info?.lastName || '';
                    const name = user.name || (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '');
                    return {
                        uid: user.id || user.uid || user._id || '',
                        email: user.email || '',
                        active: user.active ?? user.info?.active ?? true,
                        admin: user.admin ?? user.info?.admin ?? false,
                        firstName,
                        lastName,
                        organization: user.organization || user.info?.organization,
                        info: { ...user.info, name },
                    };
                });

                setUsers(usersData.sort((a, b) => {
                    const nameA = a.info?.name || `${a.firstName} ${a.lastName}`.trim() || a.uid || '';
                    const nameB = b.info?.name || `${b.firstName} ${b.lastName}`.trim() || b.uid || '';
                    return nameA.localeCompare(nameB);
                }));
            } catch (err: any) {
                console.error('Error fetching users:', err);
                setError(err.message || 'Failed to fetch users from API');
            }
        };

        if (isAdmin) {
            fetchUsers();
        }
    }, [isAdmin]);

    const handleImpersonate = async () => {
        if (!selectedUserId) {
            setError('Please select a user');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            setIsDialogOpen(false);
            setSelectedUserId('');
            await impersonateUserByUid(selectedUserId);
            return;
        } catch (err: any) {
            console.error('Error impersonating user:', err);
            setError(err.message || 'Failed to impersonate user');
        } finally {
            setIsLoading(false);
        }
    };

    const collectionTiles = useMemo(() => {
        const list = Array.isArray(components) ? components : [];
        return list
            .filter((component: { slug?: string }) => (component.slug?.toLowerCase() || '') !== 'users')
            .slice()
            .sort((a: { name?: string; slug?: string }, b: { name?: string; slug?: string }) =>
                String(a.name || a.slug || '').localeCompare(String(b.name || b.slug || ''))
            );
    }, [components]);

    const openCollection = (component: { slug?: string; organization?: string | null }) => {
        const slug = typeof component.slug === 'string' ? component.slug.replace(/[!]+$/, '').trim() : '';
        if (!slug) return;
        navigate(buildCollectionPath({ slug, organization: component.organization }));
    };

    const collectionTilesSection = (
        <div className="w-full min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Collections
            </h2>
            {componentsLoading && collectionTiles.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
            )}
            {componentsError && (
                <Alert variant="destructive">
                    <AlertDescription>
                        {typeof componentsError === 'string' ? componentsError : 'Failed to load collections'}
                    </AlertDescription>
                </Alert>
            )}
            {!componentsLoading && !componentsError && collectionTiles.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400">No collections yet.</p>
            )}
            {collectionTiles.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {collectionTiles.map((component: {
                        uid?: string;
                        name?: string;
                        slug?: string;
                        organization?: string | null;
                        menuIcon?: string;
                    }) => {
                        const name = component.name || component.slug || 'Collection';
                        const slug = typeof component.slug === 'string' ? component.slug.replace(/[!]+$/, '').trim() : '';
                        return (
                            <Card
                                key={component.uid || slug || name}
                                role="button"
                                tabIndex={slug ? 0 : -1}
                                className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-shadow cursor-pointer hover:shadow-md"
                                onClick={() => openCollection(component)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openCollection(component);
                                    }
                                }}
                            >
                                <CardHeader className="pb-4">
                                    <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        <CollectionMenuIcon
                                            menuIcon={component.menuIcon}
                                            className="w-4 h-4 shrink-0 text-violet-700 dark:text-violet-300"
                                        />
                                        <span className="truncate">{name}</span>
                                    </CardTitle>
                                </CardHeader>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const isCollapsedSubmissions =
        !showAllSubmissions && formSubmissions.length > 7;

    const visibleSubmissions = isCollapsedSubmissions
        ? formSubmissions.slice(0, 7)
        : formSubmissions;

    const recentSubmissionsSection = (
        <div className="w-full min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Recent submissions
            </h2>
            {formSubmissionsLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
            )}
            {formSubmissionsError && (
                <Alert variant="destructive">
                    <AlertDescription>{formSubmissionsError}</AlertDescription>
                </Alert>
            )}
            {!formSubmissionsLoading && !formSubmissionsError && formSubmissions.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400">You have not submitted any forms yet.</p>
            )}
            {!formSubmissionsLoading && !formSubmissionsError && formSubmissions.length > 0 && (
                <div
                    className={
                        inboxItems.length > 0
                            ? 'grid grid-cols-1 gap-4'
                            : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                    }
                >
                    {visibleSubmissions.map((entry) => {
                        const isArchived = Boolean(entry.archived);
                        const card = (
                            <Card
                                role="button"
                                tabIndex={isArchived ? -1 : 0}
                                aria-disabled={isArchived}
                                className={`w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-shadow flex flex-col h-full ${isArchived
                                    ? 'opacity-60 cursor-not-allowed'
                                    : 'cursor-pointer hover:shadow-md'
                                    }`}
                                onClick={() => {
                                    if (isArchived) return;
                                    navigate(`/my-submissions/${entry.formToken}/${entry.uid}`);
                                }}
                                onKeyDown={(e) => {
                                    if (isArchived) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate(`/my-submissions/${entry.formToken}/${entry.uid}`);
                                    }
                                }}
                            >
                                <CardHeader className="flex-1 pb-2">
                                    <SubmissionListEntryContent
                                        entry={entry}
                                        variant="dashboard"
                                        showFooter={false}
                                    />
                                </CardHeader>
                                {(entry.footer || isArchived) && (
                                    <CardContent className="px-6 pt-3 pb-2 mt-auto space-y-1">
                                        {entry.footer ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 break-words">
                                                {entry.footer}
                                            </p>
                                        ) : null}
                                        {isArchived && (
                                            <p className="text-xs font-medium text-red-700 dark:text-red-400">
                                                Archived
                                            </p>
                                        )}
                                    </CardContent>
                                )}
                            </Card>
                        );
                        return (
                            <Fragment key={entry.uid}>
                                {isArchived ? (
                                    <TooltipProvider delayDuration={300}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="block h-full">{card}</div>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs">
                                                This submission was archived. It stays in your list for
                                                reference but cannot be opened.
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                ) : (
                                    card
                                )}
                            </Fragment>
                        );
                    })}
                    {formSubmissions.length > 7 && (
                        <Card
                            className="w-full flex items-stretch justify-center border-dashed border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => setShowAllSubmissions((prev) => !prev)}
                        >
                            <CardContent className="flex items-center justify-center h-full w-full py-6">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 text-center">
                                    {showAllSubmissions ? 'Show less' : 'Show more'}
                                </span>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );

    const InboxTile: React.FC = () => {
        if (inboxItems.length === 0) return null;

        const previewItems = inboxItems.slice(0, 3);
        const remainingCount = inboxItems.length - previewItems.length;

        return (
            <Card className="w-full h-full border-violet-100/70 dark:border-violet-900/60 bg-gradient-to-br from-violet-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 shadow-md">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold text-violet-900 dark:text-violet-100">
                        <Inbox className="w-4 h-4" />
                        Inbox
                        <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-violet-600 text-white text-xs font-medium tabular-nums">
                            {inboxItems.length}
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-slate-700 dark:text-slate-200">
                        {inboxItems.length === 1
                            ? '1 capture waiting to be moved into a collection.'
                            : `${inboxItems.length} captures waiting to be moved into a collection.`}
                    </p>
                    <ul className="space-y-2">
                        {previewItems.map((item) => (
                            <li
                                key={item.uid}
                                className="rounded-md border border-violet-200/80 dark:border-violet-800/60 bg-white/70 dark:bg-slate-900/50 px-3 py-2 space-y-2"
                            >
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {formatInboxDate(item.createdAt)}
                                </p>
                                <p className="text-sm text-slate-800 dark:text-slate-100 line-clamp-2 whitespace-pre-wrap break-words">
                                    {item.text}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="h-7 px-2.5 text-xs bg-violet-100 text-violet-900 hover:bg-violet-200 border border-violet-300 dark:bg-violet-900 dark:text-violet-50 dark:hover:bg-violet-800 dark:border-violet-700"
                                        onClick={() => setInboxAssignItem(item)}
                                    >
                                        Assign
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2.5 text-xs"
                                        onClick={() => setInboxDeleteItem(item)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                    {remainingCount > 0 ? (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            +{remainingCount} more in inbox
                        </p>
                    ) : null}
                    <Button
                        size="sm"
                        className="w-full justify-center bg-violet-100 text-violet-900 hover:bg-violet-200 border border-violet-300 dark:bg-violet-900 dark:text-violet-50 dark:hover:bg-violet-800 dark:border-violet-700"
                        onClick={() => navigate('/inbox')}
                    >
                        Open inbox
                    </Button>
                </CardContent>
            </Card>
        );
    };

    const GetInTouchCard: React.FC = () => (
        <Card className="w-full h-full border-blue-100/70 dark:border-blue-900/60 bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 shadow-md">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
                    <Mail className="w-4 h-4" />
                    Get in touch
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">
                    Interested in using Labwick for your event, organisation, team or lab?
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                    Leave your details and we will get in touch soon.
                </p>
                <Button
                    size="sm"
                    className="w-full justify-center bg-blue-100 text-blue-900 hover:bg-blue-200 border border-blue-300 dark:bg-blue-900 dark:text-blue-50 dark:hover:bg-blue-800 dark:border-blue-700"
                    onClick={() => setContactDialogOpen(true)}
                >
                    Get in touch
                </Button>
            </CardContent>
        </Card>
    );

    const CreateCollectionCard: React.FC = () => {
        if (!canCreateCollection) return null;
        return (
            <Card className="w-full h-full border-emerald-100/70 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 shadow-md">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                        <Plus className="w-4 h-4" />
                        New collection
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-slate-700 dark:text-slate-200 mb-4">
                        Define a new data collection for your workspace.
                    </p>
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="sm"
                                    className="w-full justify-center bg-emerald-100 text-emerald-900 hover:bg-emerald-200 border border-emerald-300 dark:bg-emerald-900 dark:text-emerald-50 dark:hover:bg-emerald-800 dark:border-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={navigateCreateCollection}
                                    disabled={!canCreateCollectionByLimit}
                                >
                                    Create collection
                                </Button>
                            </TooltipTrigger>
                            {!canCreateCollectionByLimit && (
                                <TooltipContent className="text-base px-4 py-2 max-w-xs">
                                    <p>
                                        {hasNoLimit
                                            ? 'You cannot create collections yet. Ask an administrator to assign a collection limit.'
                                            : 'You have reached your collection limit.'}
                                    </p>
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                </CardContent>
            </Card>
        );
    };

    const Client404InsightsCard: React.FC = () => {
        const [stats, setStats] = useState<Client404Stats | null>(null);
        const [loading404, setLoading404] = useState(true);
        const [clearing404, setClearing404] = useState(false);
        const [error404, setError404] = useState<string | null>(null);
        const [pathBreakdownOpen, setPathBreakdownOpen] = useState(false);
        const [pathBreakdownLoading, setPathBreakdownLoading] = useState(false);
        const [pathBreakdownError, setPathBreakdownError] = useState<string | null>(null);
        const [pathBreakdown, setPathBreakdown] = useState<Client404PathBreakdown | null>(null);

        const refresh404AfterClear = () =>
            fetchClient404Stats()
                .then((s) => {
                    setStats(s);
                    setError404(null);
                })
                .catch((e: unknown) => {
                    setError404(e instanceof Error ? e.message : 'Failed to load 404 stats');
                });

        useEffect(() => {
            if (!isAdmin) return;
            let cancelled = false;
            setLoading404(true);
            fetchClient404Stats()
                .then((s) => {
                    if (!cancelled) {
                        setStats(s);
                        setError404(null);
                    }
                })
                .catch((e: unknown) => {
                    if (!cancelled) {
                        setStats(null);
                        setError404(e instanceof Error ? e.message : 'Failed to load 404 stats');
                    }
                })
                .finally(() => {
                    if (!cancelled) setLoading404(false);
                });
            return () => {
                cancelled = true;
            };
        }, [isAdmin]);

        const handleClear404Log = () => {
            if (!stats || stats.totalHits === 0) return;
            if (
                !window.confirm(
                    'Remove all recorded client 404 hits? This cannot be undone.',
                )
            ) {
                return;
            }
            setClearing404(true);
            setError404(null);
            void clearClient404Log()
                .then((r) => {
                    toast.success(
                        r.deletedCount > 0
                            ? `Cleared ${r.deletedCount} hit(s).`
                            : 'Log cleared.',
                    );
                    return refresh404AfterClear();
                })
                .catch((e: unknown) => {
                    toast.error(e instanceof Error ? e.message : 'Failed to clear log');
                })
                .finally(() => {
                    setClearing404(false);
                });
        };

        const openPathBreakdown = (path: string) => {
            setPathBreakdownOpen(true);
            setPathBreakdownLoading(true);
            setPathBreakdownError(null);
            setPathBreakdown(null);
            void fetchClient404PathBreakdown(path)
                .then((d) => setPathBreakdown(d))
                .catch((e: unknown) =>
                    setPathBreakdownError(
                        e instanceof Error ? e.message : 'Failed to load breakdown',
                    ),
                )
                .finally(() => setPathBreakdownLoading(false));
        };

        return (
            <>
                <Card className="w-full h-full border-amber-100/70 dark:border-amber-900/50 bg-gradient-to-br from-amber-50/90 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 shadow-md">
                    <CardHeader className="pb-2 flex flex-row flex-wrap items-start justify-between gap-2 space-y-0">
                        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
                            <Radio className="w-4 h-4" />
                            Missing pages (404)
                        </CardTitle>
                        {!loading404 && !error404 && stats && stats.totalHits > 0 ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0 text-xs h-8 border-amber-300/80 text-amber-900 dark:text-amber-100 dark:border-amber-700"
                                disabled={clearing404}
                                onClick={handleClear404Log}
                            >
                                {clearing404 ? 'Clearing…' : 'Clear log'}
                            </Button>
                        ) : null}
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <p className="text-slate-600 dark:text-slate-300">
                            Logged when someone opens an unknown route or collection in the web app.
                        </p>
                        {loading404 && <p className="text-slate-500 dark:text-slate-400">Loading…</p>}
                        {error404 && (
                            <Alert variant="destructive">
                                <AlertDescription>{error404}</AlertDescription>
                            </Alert>
                        )}
                        {!loading404 && !error404 && stats && (
                            <>
                                <div className="rounded-md border border-amber-200/80 dark:border-amber-800/60 bg-white/70 dark:bg-slate-900/50 px-3 py-2">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                        Total hits
                                    </p>
                                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                                        {stats.totalHits}
                                    </p>
                                </div>
                                <div className="rounded-md border border-amber-200/80 dark:border-amber-800/60 bg-white/70 dark:bg-slate-900/50 px-3 py-2 space-y-1">
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                        Most recent
                                    </p>
                                    {stats.lastHit ? (
                                        <>
                                            <button
                                                type="button"
                                                className="font-mono text-xs text-amber-800 dark:text-amber-200 break-all text-left w-full hover:underline"
                                                onClick={() => openPathBreakdown(stats.lastHit!.path)}
                                            >
                                                {stats.lastHit.path}
                                            </button>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                {format404HitTime(stats.lastHit.at)} · {stats.lastHit.kind.replace(/_/g, ' ')}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-xs text-slate-500 dark:text-slate-400">No hits recorded yet.</p>
                                    )}
                                </div>
                                {stats.byPath.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                                            Top URLs
                                            <span className="font-normal text-slate-400 dark:text-slate-500">
                                                {' '}
                                                · click a path for sign-in vs public hits
                                            </span>
                                        </p>
                                        <ul className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                                            {stats.byPath.slice(0, 8).map((row) => (
                                                <li
                                                    key={row.path}
                                                    className="flex justify-between gap-2 text-xs text-slate-700 dark:text-slate-200"
                                                >
                                                    <button
                                                        type="button"
                                                        className="font-mono truncate text-left min-w-0 text-amber-800 dark:text-amber-200 hover:underline"
                                                        title={row.path}
                                                        onClick={() => openPathBreakdown(row.path)}
                                                    >
                                                        {row.path}
                                                    </button>
                                                    <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                                                        ×{row.count}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                <Dialog
                    open={pathBreakdownOpen}
                    onOpenChange={(open) => {
                        setPathBreakdownOpen(open);
                        if (!open) {
                            setPathBreakdownError(null);
                            setPathBreakdown(null);
                        }
                    }}
                >
                    <DialogContent className="sm:max-w-lg max-h-[min(90vh,520px)] flex flex-col">
                        <DialogHeader>
                            <DialogTitle>404 path breakdown</DialogTitle>
                            <DialogDescription className="font-mono text-xs break-all text-slate-700 dark:text-slate-300">
                                {pathBreakdown?.path ?? '—'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 overflow-y-auto flex-1 text-sm py-1">
                            {pathBreakdownLoading && (
                                <p className="text-slate-500 dark:text-slate-400">Loading…</p>
                            )}
                            {pathBreakdownError && (
                                <Alert variant="destructive">
                                    <AlertDescription>{pathBreakdownError}</AlertDescription>
                                </Alert>
                            )}
                            {!pathBreakdownLoading && !pathBreakdownError && pathBreakdown ? (
                                <>
                                    <div className="rounded-md border border-amber-200/80 dark:border-amber-800/60 bg-white/70 dark:bg-slate-900/50 px-3 py-2 space-y-1">
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                            Public access
                                        </p>
                                        <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                                            {pathBreakdown.publicHits}{' '}
                                            <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                                                hit{pathBreakdown.publicHits === 1 ? '' : 's'}
                                            </span>
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                                            While signed out, or logged before user tracking was enabled on this log.
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                                            Signed-in users ({pathBreakdown.byUser.length})
                                        </p>
                                        {pathBreakdown.byUser.length === 0 ? (
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                No hits attributed to signed-in users for this path.
                                            </p>
                                        ) : (
                                            <ul className="space-y-2 max-h-48 overflow-y-auto pr-1 border border-slate-200 dark:border-slate-700 rounded-md p-2">
                                                {pathBreakdown.byUser.map((u) => (
                                                    <li
                                                        key={u.viewerUid}
                                                        className="text-xs border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0 last:pb-0"
                                                    >
                                                        <p className="font-mono break-all text-slate-800 dark:text-slate-200">
                                                            {u.viewerUid}
                                                        </p>
                                                        {u.viewerEmail ? (
                                                            <p className="text-slate-600 dark:text-slate-400 truncate">
                                                                {u.viewerEmail}
                                                            </p>
                                                        ) : null}
                                                        <p className="text-slate-500 dark:text-slate-400 mt-0.5">
                                                            {u.hitCount} hit{u.hitCount === 1 ? '' : 's'} · last{' '}
                                                            {format404HitTime(u.lastAt)}
                                                        </p>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </DialogContent>
                </Dialog>
            </>
        );
    };

    const AdminToolsCard: React.FC<{ error: string | null; onOpenImpersonate: () => void }> = ({
        error,
        onOpenImpersonate,
    }) => (
        <Card className="w-full h-full border-blue-100/70 dark:border-blue-900/60 bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 shadow-md">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
                    <UserCog className="w-4 h-4" />
                    Admin Tools
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-slate-700 dark:text-slate-200">
                    Administrative actions for managing users and troubleshooting accounts.
                </p>
                <Button
                    onClick={onOpenImpersonate}
                    size="sm"
                    className="w-full justify-center bg-blue-100 text-blue-900 hover:bg-blue-200 border border-blue-300 dark:bg-blue-900 dark:text-blue-50 dark:hover:bg-blue-800 dark:border-blue-700"
                >
                    Impersonate user
                </Button>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );

    const showRecentSubmissions = !hideSubmissions && !isWebsiteOwner;
    const showGetInTouch = !isWebsiteOwner;
    const showInboxTile = !hideInbox && inboxItems.length > 0;
    const submissionsAndInboxRow = !showRecentSubmissions && !showInboxTile ? null : (
        <div
            className={
                showRecentSubmissions && showInboxTile
                    ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 items-start'
                    : undefined
            }
        >
            {showRecentSubmissions ? (
                <div className={showInboxTile ? 'min-w-0 w-full' : undefined}>
                    {recentSubmissionsSection}
                </div>
            ) : null}
            {showInboxTile ? <InboxTile /> : null}
        </div>
    );

    const dashboardCardsGrid = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <QuickAddDashboardSetting userId={userId} />
            {showGetInTouch ? <GetInTouchCard /> : null}
            <CreateCollectionCard />
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h1>

            {isAdmin ? (
                <div className="w-full max-w-5xl mx-auto space-y-10">
                    {isWebsiteOwner ? collectionTilesSection : null}
                    {submissionsAndInboxRow}
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            <Person2 /> Admin
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <QuickAddDashboardSetting userId={userId} />
                            {showGetInTouch ? <GetInTouchCard /> : null}
                            <CreateCollectionCard />
                            <Client404InsightsCard />
                            <AdminToolsCard
                                error={error}
                                onOpenImpersonate={() => setIsDialogOpen(true)}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {isWebsiteOwner ? collectionTilesSection : null}
                    {submissionsAndInboxRow}
                    {dashboardCardsGrid}
                </div>
            )}

            <AssignInboxItemDialog
                open={Boolean(inboxAssignItem)}
                onOpenChange={(open) => {
                    if (!open) setInboxAssignItem(null);
                }}
                inboxItem={inboxAssignItem}
                onAssigned={() => {
                    if (inboxAssignItem) removeInboxItemFromList(inboxAssignItem.uid);
                    setInboxAssignItem(null);
                    window.dispatchEvent(new CustomEvent('inbox-updated'));
                }}
                onStaleItem={() => {
                    if (inboxAssignItem) removeInboxItemFromList(inboxAssignItem.uid);
                    setInboxAssignItem(null);
                }}
            />

            <Dialog open={Boolean(inboxDeleteItem)} onOpenChange={(open) => !open && setInboxDeleteItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete inbox item?</DialogTitle>
                        <DialogDescription>
                            This will permanently remove the item from your inbox.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setInboxDeleteItem(null)}
                            disabled={inboxDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => void handleInboxDeleteConfirm()}
                            disabled={inboxDeleting}
                        >
                            {inboxDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDialogOpen} onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) {
                    setSelectedUserId('');
                    setError(null);
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Impersonate User</DialogTitle>
                        <DialogDescription>
                            Select a user to impersonate. You will be logged in as that user and redirected to the home page.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Select User</label>
                            <Select
                                value={selectedUserId}
                                onValueChange={setSelectedUserId}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a user..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map((user) => {
                                        const displayName = user.info?.name ||
                                            (user.firstName || user.lastName
                                                ? `${[user.firstName, user.lastName].filter(Boolean).join(' ')}`
                                                : user.uid || 'Unknown User');
                                        const value = user.uid || '';
                                        return (
                                            <SelectItem key={user.uid} value={value}>
                                                {displayName}
                                                {user.email ? ` (${user.email})` : ''}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsDialogOpen(false);
                                    setSelectedUserId('');
                                    setError(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleImpersonate}
                                disabled={!selectedUserId || isLoading}
                            >
                                {isLoading ? 'Generating...' : 'Impersonate'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Get in touch</DialogTitle>
                        <DialogDescription>
                            Leave your email and we will get in touch soon.
                        </DialogDescription>
                    </DialogHeader>
                    <GetInTouchPanel
                        source="labwick-dashboard"
                        onClose={() => setContactDialogOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Home;
